import React, {
  useEffect,
  useState,
} from "react";

import "./Agents.css";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import {
  FaUsers,
  FaFileAlt,
  FaClipboardList,
  FaQrcode,
  FaCoins,
  FaUserPlus,
  FaWallet,
  FaMoneyBillWave,
  FaFutbol,
  FaChartLine,
  FaArrowRight,
  FaArrowLeft,
  FaBolt,
  FaCrown,
  FaGift,
  FaUserFriends,
} from "react-icons/fa";

import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from "firebase/firestore";

import { db } from "../firebase";

import AgentIntro from "./AgentIntro";


/* =========================================================
   LOCAL STORAGE
========================================================= */

const AGENT_CACHE_KEY =
  "bfc_agent_cache";


/* =========================================================
   FORMAT MONEY
========================================================= */

const formatMoney = (value) => {

  const number =
    Number(value || 0);

  return number.toLocaleString(
    "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

};


/* =========================================================
   NORMALIZE CURRENCY
=========================================================

   Number:
   5000

   becomes:

   {
     TZS: 5000
   }

   Object:
   {
     TZS: 5000,
     USD: 10
   }

   remains object.
========================================================= */

const normalizeCurrency = (
  value,
  defaultCurrency = "TZS"
) => {

  /* =====================================================
     NUMBER
  ===================================================== */

  if (
    typeof value === "number" ||
    typeof value === "string"
  ) {

    const number =
      Number(value || 0);

    if (!number) {
      return {};
    }

    return {
      [defaultCurrency]: number,
    };

  }


  /* =====================================================
     OBJECT
  ===================================================== */

  if (
    value &&
    typeof value === "object"
  ) {

    return Object.entries(value)
      .reduce(
        (
          result,
          [currency, amount]
        ) => {

          const number =
            Number(amount || 0);

          if (number !== 0) {

            result[currency] =
              number;

          }

          return result;

        },
        {}
      );

  }


  return {};

};


/* =========================================================
   ADD CURRENCY
========================================================= */

const addCurrency = (
  first = {},
  second = {}
) => {

  const result = {
    ...normalizeCurrency(first),
  };


  Object.entries(
    normalizeCurrency(second)
  ).forEach(
    ([currency, amount]) => {

      result[currency] =
        Number(
          result[currency] || 0
        ) +
        Number(amount || 0);

    }
  );


  return result;

};


/* =========================================================
   FORMAT CURRENCY
========================================================= */

const formatCurrency = (
  value
) => {

  const normalized =
    normalizeCurrency(value);


  const entries =
    Object.entries(
      normalized
    );


  if (!entries.length) {

    return "0.00";

  }


  return entries
    .map(
      ([currency, amount]) =>
        `${formatMoney(amount)} ${currency}`
    )
    .join(" + ");

};


/* =========================================================
   GET DATE VALUE
========================================================= */

const getDateValue = (
  value
) => {

  if (!value) {
    return 0;
  }


  /* Firestore Timestamp */

  if (
    typeof value?.toDate ===
    "function"
  ) {

    return value
      .toDate()
      .getTime();

  }


  if (
    typeof value?.seconds ===
    "number"
  ) {

    return (
      value.seconds * 1000
    );

  }


  /* JS Date */

  if (
    value instanceof Date
  ) {

    return value.getTime();

  }


  /* Number */

  if (
    typeof value === "number"
  ) {

    return value > 100000000000
      ? value
      : value * 1000;

  }


  /* String */

  const date =
    new Date(value);


  return Number.isNaN(
    date.getTime()
  )
    ? 0
    : date.getTime();

};


/* =========================================================
   GET MONTH VALUE
========================================================= */

const getMonthValue = (
  item
) => {

  const year =
    Number(item?.year || 0);

  const month =
    Number(item?.month || 0);


  if (
    year > 0 &&
    month > 0
  ) {

    return (
      year * 100 +
      month
    );

  }


  return getDateValue(
    item?.approvedAt ||
    item?.updatedAt ||
    item?.createdAt
  );

};


/* =========================================================
   READ AGENT CACHE
========================================================= */

const getAgentCache = (
  username
) => {

  try {

    const saved =
      localStorage.getItem(
        AGENT_CACHE_KEY
      );


    if (!saved) {
      return null;
    }


    const parsed =
      JSON.parse(saved);


    if (
      !parsed ||
      parsed.username !== username
    ) {

      return null;

    }


    return parsed;

  } catch (error) {

    console.error(
      "Agent cache read error:",
      error
    );

    return null;

  }

};


/* =========================================================
   SAVE AGENT CACHE
========================================================= */

const saveAgentCache = (
  username,
  data
) => {

  if (!username) {
    return;
  }


  try {

    const cache = {

      username,

      totalCommission:
        normalizeCurrency(
          data.totalCommission
        ),

      promotionBonus:
        normalizeCurrency(
          data.promotionBonus
        ),

      updatedAt:
        Date.now(),

    };


    localStorage.setItem(
      AGENT_CACHE_KEY,
      JSON.stringify(cache)
    );


    /*
      Hii ni kwa same tab.

      storage event haisababishi
      update kwenye tab ile ile.
    */

    window.dispatchEvent(
      new CustomEvent(
        "bfc-agent-cache-updated"
      )
    );


  } catch (error) {

    console.error(
      "Agent cache save error:",
      error
    );

  }

};


/* =========================================================
   BACK BUTTON
========================================================= */

const BackButton = () => {

  const navigate =
    useNavigate();


  return (

    <button
      type="button"
      className="agency-back-btn"
      onClick={() =>
        navigate(-1)
      }
    >

      <FaArrowLeft />

      <span>
        Back
      </span>

    </button>

  );

};


/* =========================================================
   AGENT
========================================================= */

const Agent = () => {

  const [
    userData,
    setUserData,
  ] = useState(null);


  /* =======================================================
     COMMISSION
  ======================================================= */

  const [
    totalCommission,
    setTotalCommission,
  ] = useState({});


  /* =======================================================
     MONTHLY BONUS
  ======================================================= */

  const [
    promotionBonus,
    setPromotionBonus,
  ] = useState({});


  /* =======================================================
     INTRO
  ======================================================= */

  const [
    showIntro,
    setShowIntro,
  ] = useState(true);


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    dataLoading,
    setDataLoading,
  ] = useState(true);


  /* =======================================================
     USERNAME
  ======================================================= */

  const storedUsername =
    (
      localStorage.getItem(
        "username"
      ) || ""
    ).trim();


  /* =======================================================
     CHECK INTRO
  ======================================================= */

  useEffect(() => {

    const introSeen =
      localStorage.getItem(
        "agentIntroSeen"
      );


    if (introSeen) {

      setShowIntro(false);

    }

  }, []);


  /* =======================================================
     LOAD LOCAL STORAGE FIRST
  ======================================================= */

  useEffect(() => {

    if (!storedUsername) {

      setDataLoading(false);

      return;

    }


    const cache =
      getAgentCache(
        storedUsername
      );


    if (!cache) {
      return;
    }


    /*
      Commission kutoka localStorage
    */

    setTotalCommission(
      normalizeCurrency(
        cache.totalCommission
      )
    );


    /*
      Monthly bonus kutoka localStorage
    */

    setPromotionBonus(
      normalizeCurrency(
        cache.promotionBonus
      )
    );


  }, [
    storedUsername,
  ]);


  /* =======================================================
     LISTEN TO LOCAL STORAGE CHANGES
  ======================================================= */

  useEffect(() => {

    const updateFromCache =
      () => {

        if (!storedUsername) {
          return;
        }


        const cache =
          getAgentCache(
            storedUsername
          );


        if (!cache) {
          return;
        }


        setTotalCommission(
          normalizeCurrency(
            cache.totalCommission
          )
        );


        setPromotionBonus(
          normalizeCurrency(
            cache.promotionBonus
          )
        );

      };


    /*
      Same tab
    */

    window.addEventListener(
      "bfc-agent-cache-updated",
      updateFromCache
    );


    /*
      Other tabs
    */

    window.addEventListener(
      "storage",
      updateFromCache
    );


    return () => {

      window.removeEventListener(
        "bfc-agent-cache-updated",
        updateFromCache
      );


      window.removeEventListener(
        "storage",
        updateFromCache
      );

    };

  }, [
    storedUsername,
  ]);


  /* =======================================================
     FETCH USER
  ======================================================= */

  useEffect(() => {

    if (
      showIntro ||
      !storedUsername
    ) {

      return;

    }


    const fetchUser =
      async () => {

        try {

          const usersRef =
            collection(
              db,
              "users"
            );


          const userQuery =
            query(
              usersRef,
              where(
                "username",
                "==",
                storedUsername
              )
            );


          const userSnap =
            await getDocs(
              userQuery
            );


          if (
            !userSnap.empty
          ) {

            const data =
              userSnap
                .docs[0]
                .data();


            setUserData(
              data
            );

          }

        } catch (error) {

          console.error(
            "Error fetching user:",
            error
          );

        }

      };


    fetchUser();

  }, [
    showIntro,
    storedUsername,
  ]);


  /* =======================================================
     REAL-TIME COMMISSION
  =======================================================

     COLLECTION:

       admin_commissions

     FIELD:

       total

     Example:

       total: {
         TZS: 6352.7734642
       }

     Tunatumia TOTAL, sio editedTotal.
  ======================================================= */

  useEffect(() => {

    if (
      showIntro ||
      !storedUsername
    ) {

      return;

    }


    const commissionsRef =
      collection(
        db,
        "admin_commissions"
      );


    const commissionQuery =
      query(
        commissionsRef,
        where(
          "username",
          "==",
          storedUsername
        )
      );


    const unsubscribe =
      onSnapshot(
        commissionQuery,

        (snapshot) => {

          let commissionTotal =
            {};


          snapshot.forEach(
            (commissionDoc) => {

              const data =
                commissionDoc.data();


              /*
                Tunataka successful
                commissions tu.

                Mfano:

                status: "success"
              */

              const status =
                String(
                  data.status || ""
                ).toLowerCase();


              if (
                status !== "success"
              ) {

                return;

              }


              /*
                MUHIMU:

                Tunatumia FIELD:

                  total

                SIO:

                  editedTotal
              */

              const total =
                data.total;


              if (!total) {
                return;
              }


              commissionTotal =
                addCurrency(
                  commissionTotal,
                  total
                );

            }
          );


          /*
            UPDATE UI
          */

          setTotalCommission(
            commissionTotal
          );


          setDataLoading(
            false
          );

        },

        (error) => {

          console.error(
            "Commission listener error:",
            error
          );


          /*
            LocalStorage itaendelea
            kuonyesha data ya mwisho.
          */

          setDataLoading(
            false
          );

        }
      );


    return () => {

      unsubscribe();

    };

  }, [
    showIntro,
    storedUsername,
  ]);


  /* =======================================================
     REAL-TIME MONTHLY BONUS
  =======================================================

     COLLECTION:

       monthly_bonuses

     Tunajumlisha BONUS ZOTE za username.

     Hatuchukui latest bonus pekee.
  ======================================================= */

  useEffect(() => {

    if (
      showIntro ||
      !storedUsername
    ) {

      return;

    }


    const bonusRef =
      collection(
        db,
        "monthly_bonuses"
      );


    const bonusQuery =
      query(
        bonusRef,
        where(
          "username",
          "==",
          storedUsername
        )
      );


    const unsubscribe =
      onSnapshot(
        bonusQuery,

        (snapshot) => {

          let totalBonus =
            {};


          snapshot.forEach(
            (bonusDoc) => {

              const data =
                bonusDoc.data();


              /*
                Tunatarajia:

                  bonusAmount

                inaweza kuwa:

                  {
                    TZS: 5000
                  }

                AU:

                  bonusAmount: 5000
                  currency: "TZS"
              */

              let amount =
                data.bonusAmount;


              /*
                Kama project yako ina field
                amount badala ya bonusAmount,
                tuna-support pia.
              */

              if (
                amount === undefined ||
                amount === null
              ) {

                amount =
                  data.amount;

              }


              /*
                OBJECT

                {
                  TZS: 5000
                }
              */

              if (
                amount &&
                typeof amount === "object"
              ) {

                totalBonus =
                  addCurrency(
                    totalBonus,
                    amount
                  );

                return;

              }


              /*
                NUMBER

                bonusAmount: 5000
                currency: "TZS"
              */

              const number =
                Number(
                  amount || 0
                );


              if (
                number === 0
              ) {

                return;

              }


              const currency =
                data.currency ||
                "TZS";


              totalBonus =
                addCurrency(
                  totalBonus,
                  {
                    [currency]:
                      number,
                  }
                );

            }
          );


          /*
            UPDATE UI

            Hapa ndiyo monthly bonus
            ya JUMLA ya records zote.
          */

          setPromotionBonus(
            totalBonus
          );


          setDataLoading(
            false
          );

        },

        (error) => {

          console.error(
            "Monthly bonus listener error:",
            error
          );


          /*
            Cached value itaendelea
            kuonekana.
          */

          setDataLoading(
            false
          );

        }
      );


    return () => {

      unsubscribe();

    };

  }, [
    showIntro,
    storedUsername,
  ]);


  /* =======================================================
     SAVE CURRENT DATA TO LOCAL STORAGE
  ======================================================= */

  useEffect(() => {

    if (!storedUsername) {
      return;
    }


    saveAgentCache(
      storedUsername,
      {
        totalCommission:
          totalCommission,

        promotionBonus:
          promotionBonus,
      }
    );

  }, [
    totalCommission,
    promotionBonus,
    storedUsername,
  ]);


  /* =======================================================
     CONTINUE INTRO
  ======================================================= */

  const handleContinue =
    () => {

      localStorage.setItem(
        "agentIntroSeen",
        "true"
      );


      setShowIntro(
        false
      );

    };


  /* =======================================================
     RESET INTRO
  ======================================================= */

  const handleResetIntro =
    () => {

      localStorage.removeItem(
        "agentIntroSeen"
      );


      setShowIntro(
        true
      );

    };


  /* =======================================================
     SHOW INTRO
  ======================================================= */

  if (showIntro) {

    return (

      <AgentIntro
        onContinue={
          handleContinue
        }
      />

    );

  }


  /* =======================================================
     USERNAME
  ======================================================= */

  const username =
    userData?.username ||
    storedUsername ||
    "Agent";


  const initial =
    username
      ?.charAt(0)
      ?.toUpperCase() ||
    "A";


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="agency-page">

      <div className="agency-container">


        {/* =================================================
            TOP NAV
        ================================================= */}

        <div className="agency-topbar">

          <BackButton />


          <div className="agency-top-title">

            <span>
              BFC
            </span>

            <strong>
              AGENCY CENTER
            </strong>

          </div>


          <div className="agency-live">

            <i />

            LIVE

          </div>

        </div>


        {/* =================================================
            HERO
        ================================================= */}

        <section className="agency-hero">

          <div className="agency-hero-glow" />


          <div className="agency-hero-content">

            <div className="agency-avatar">

              {initial}

              <span>
                <FaCrown />
              </span>

            </div>


            <div className="agency-welcome">

              <span>
                WELCOME BACK
              </span>

              <h1>
                {username}
              </h1>

              <p>
                Manage your agency,
                team and rewards.
              </p>

            </div>

          </div>


          <div className="agency-hero-badge">

            <FaBolt />

            <div>

              <span>
                AGENT STATUS
              </span>

              <strong>
                ACTIVE
              </strong>

            </div>

          </div>

        </section>


        {/* =================================================
            MAIN BALANCE
        ================================================= */}

        <section className="agency-balance-card">

          <div className="balance-top">

            <div>

              <span>
                TOTAL AGENCY COMMISSION
              </span>


              <h2>
                {formatCurrency(
                  totalCommission
                )}
              </h2>

            </div>


            <div className="balance-icon">

              <FaCoins />

            </div>

          </div>


          <div className="balance-bottom">

            <div>

              <span>
                STATUS
              </span>

              <strong>
                ● Available
              </strong>

            </div>


            <div>

              <span>
                MONTHLY BONUS
              </span>

              <strong>
                {formatCurrency(
                  promotionBonus
                )}
              </strong>

            </div>

          </div>

        </section>


        {/* =================================================
            QUICK ACTIONS
        ================================================= */}

        <section className="agency-section">

          <div className="agency-section-head">

            <div>

              <span>
                QUICK ACCESS
              </span>

              <h2>
                Agency Tools
              </h2>

            </div>

            <FaBolt />

          </div>


          <div className="agency-tools">


            <Link
              to="/team"
              className="agency-tool team-tool"
            >

              <div className="tool-icon">
                <FaUsers />
              </div>

              <div className="tool-content">

                <strong>
                  My Team
                </strong>

                <span>
                  View your members
                </span>

              </div>

              <FaArrowRight
                className="tool-arrow"
              />

            </Link>


            <Link
              to="/invitecode"
              className="agency-tool invite-tool"
            >

              <div className="tool-icon">
                <FaQrcode />
              </div>

              <div className="tool-content">

                <strong>
                  Invite Friends
                </strong>

                <span>
                  Share referral code
                </span>

              </div>

              <FaArrowRight
                className="tool-arrow"
              />

            </Link>


            <Link
              to="/report"
              className="agency-tool report-tool"
            >

              <div className="tool-icon">
                <FaFileAlt />
              </div>

              <div className="tool-content">

                <strong>
                  Reports
                </strong>

                <span>
                  Check team activity
                </span>

              </div>

              <FaArrowRight
                className="tool-arrow"
              />

            </Link>


            <Link
              to="/record"
              className="agency-tool records-tool"
            >

              <div className="tool-icon">
                <FaClipboardList />
              </div>

              <div className="tool-content">

                <strong>
                  Records
                </strong>

                <span>
                  View commission history
                </span>

              </div>

              <FaArrowRight
                className="tool-arrow"
              />

            </Link>

          </div>

        </section>


        {/* =================================================
            PROMOTION
        ================================================= */}

        <section className="agency-promotion">

          <div className="promotion-decoration">

            <FaGift />

          </div>


          <div className="promotion-content">

            <span>
              SPECIAL REWARD
            </span>

            <h2>
              Promotion Bonus
            </h2>

            <p>
              Grow your team and unlock
              more rewards.
            </p>

          </div>


          <button
            type="button"
            className="promotion-button"
          >

            CLAIM

            <FaArrowRight />

          </button>

        </section>


        {/* =================================================
            DAILY DATA
        ================================================= */}

        <section className="agency-section daily-section">

          <div className="agency-section-head">

            <div>

              <span>
                TEAM ACTIVITY
              </span>

              <h2>
                Daily Data
              </h2>

            </div>

            <FaChartLine />

          </div>


          <div className="agency-data-grid">


            <Link
              to="/newregister"
              className="agency-data-card"
            >

              <div className="data-icon register">
                <FaUserPlus />
              </div>

              <strong>
                New Register
              </strong>

              <span>
                New members
              </span>

              <FaArrowRight
                className="data-arrow"
              />

            </Link>


            <Link
              to="/newdeposit"
              className="agency-data-card"
            >

              <div className="data-icon deposit">
                <FaWallet />
              </div>

              <strong>
                New Deposit
              </strong>

              <span>
                Team deposits
              </span>

              <FaArrowRight
                className="data-arrow"
              />

            </Link>


            <Link
              to="/newwithdrawals"
              className="agency-data-card"
            >

              <div className="data-icon withdrawal">
                <FaMoneyBillWave />
              </div>

              <strong>
                New Withdrawals
              </strong>

              <span>
                Team withdrawals
              </span>

              <FaArrowRight
                className="data-arrow"
              />

            </Link>


            <Link
              to="/user-gain"
              className="agency-data-card"
            >

              <div className="data-icon betting">
                <FaFutbol />
              </div>

              <strong>
                Total Bet
              </strong>

              <span>
                Team betting
              </span>

              <FaArrowRight
                className="data-arrow"
              />

            </Link>


            <Link
              to="/totalgain"
              className="agency-data-card agency-gain-card"
            >

              <div className="data-icon gain">
                <FaChartLine />
              </div>

              <strong>
                GAIN
              </strong>

              <span>
                Team performance
              </span>

              <FaArrowRight
                className="data-arrow"
              />

            </Link>


            <Link
              to="/team"
              className="agency-data-card"
            >

              <div className="data-icon members">
                <FaUserFriends />
              </div>

              <strong>
                Team
              </strong>

              <span>
                View members
              </span>

              <FaArrowRight
                className="data-arrow"
              />

            </Link>

          </div>

        </section>


        {/* =================================================
            FOOTER
        ================================================= */}

        <div className="agency-footer">

          <div className="footer-logo">
            BFC
          </div>


          <div>

            <strong>
              BFC AGENCY
            </strong>

            <span>
              Build your network. Grow together.
            </span>

          </div>

        </div>


      </div>

    </div>

  );

};


export default Agent;
